import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { tap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  AuthResponse,
  User,
  LoginResult,
  EnrolarLoginResponse,
} from '../models';
import { AprobacionService } from './aprobacion.service';
import { NotificacionService } from './notificacion.service';
import { OperadorCountsService } from './operador-counts.service';
import { SKIP_ERROR_TOAST } from './http-context.tokens';

const USER_KEY = 'suppliers_user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private currentUser = signal<User | null>(this.loadUser());

  user = this.currentUser.asReadonly();
  // El access token vive ahora en una cookie HttpOnly que el cliente NO puede leer.
  // La fuente de verdad de "hay sesión" pasa a ser el objeto `user` (no es secreto;
  // viene en el body del login y la UI lo necesita igual). Si la cookie expira en el
  // server, la próxima request da 401 y el errorInterceptor llama a logout().
  isAuthenticated = computed(() => !!this.currentUser());

  // Servicios con estado por-usuario que se limpian en el logout.
  private aprobacionService = inject(AprobacionService);
  private notificacionService = inject(NotificacionService);
  private operadorCountsService = inject(OperadorCountsService);

  constructor(private http: HttpClient, private router: Router) {}

  login(email: string, password: string) {
    return this.http.post<LoginResult>(`${environment.apiUrl}/auth/login`, { email, password })
      // Solo abre sesión si el backend ya devolvió el access_token (2FA off).
      // Si devuelve un challenge (2FA on), el componente sigue con el paso del código.
      .pipe(tap(res => { if ('access_token' in res) this.setSession(res); }));
  }

  /** Paso 2 del login 2FA: validar código TOTP (o de recuperación). */
  loginTotp(challengeToken: string, codigo: string) {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/auth/login/totp`, { challengeToken, codigo })
      .pipe(tap(res => this.setSession(res)));
  }

  /** Paso 2 del login 2FA cuando el usuario aún no tiene TOTP: confirmar enrolamiento. */
  loginTotpEnrolar(challengeToken: string, codigo: string) {
    return this.http.post<EnrolarLoginResponse>(`${environment.apiUrl}/auth/login/totp-enrolar`, { challengeToken, codigo })
      .pipe(tap(res => this.setSession(res)));
  }

  register(data: { email: string; password: string; nombre: string; apellido?: string }) {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/auth/register`, data)
      .pipe(tap(res => this.setSession(res)));
  }

  changePassword(oldPassword: string, newPassword: string) {
    return this.http.post<AuthResponse>(`${environment.apiUrl}/auth/change-password`, { oldPassword, newPassword })
      .pipe(tap(res => this.setSession(res)));
  }

  /**
   * Paso 1 del recupero self-service: dispara el envío del email con el link de reset.
   * Endpoint PÚBLICO (sin sesión). NO abre sesión ni persiste nada. El backend responde
   * de forma genérica anti-enumeración; el cliente además muestra SIEMPRE un mensaje fijo
   * (no deriva texto de la respuesta) para no degradar esa genericidad — ver LION-517.
   */
  forgotPassword(email: string) {
    // SKIP_ERROR_TOAST: el componente muestra SIEMPRE el mensaje genérico fijo; el
    // interceptor global no debe surfacear el mensaje del backend (anti-enumeración).
    return this.http.post<void>(`${environment.apiUrl}/auth/forgot-password`, { email }, {
      context: new HttpContext().set(SKIP_ERROR_TOAST, true),
    });
  }

  /**
   * Paso 2 del recupero self-service: consume el token de un solo uso (del link del email)
   * y fija la nueva contraseña. Endpoint PÚBLICO (sin sesión). NO abre sesión: el backend
   * revoca todas las sesiones, así que tras un reset OK la UI debe mandar al usuario al login.
   */
  resetPassword(token: string, newPassword: string) {
    // SKIP_ERROR_TOAST: ante token inválido/expirado el componente muestra un mensaje
    // genérico fijo; no surfaceamos el detalle del backend.
    return this.http.post<void>(`${environment.apiUrl}/auth/reset-password`, { token, newPassword }, {
      context: new HttpContext().set(SKIP_ERROR_TOAST, true),
    });
  }

  logout() {
    // Pide al backend que borre la cookie HttpOnly (el cliente no puede tocarla).
    // El endpoint es idempotente y no falla si la cookie ya no está; igual limpiamos
    // el estado local pase lo que pase (incl. error de red) para no dejar al usuario
    // "logueado" en la UI. withCredentials lo agrega el interceptor.
    this.http
      .post(`${environment.apiUrl}/auth/logout`, {})
      .subscribe({ next: () => this.clearLocalSession(), error: () => this.clearLocalSession() });
  }

  private clearLocalSession() {
    localStorage.removeItem(USER_KEY);
    this.currentUser.set(null);
    this.resetDomainState();
    this.router.navigate(['/login']);
  }

  /**
   * Limpia el estado cacheado de los servicios singleton con datos por-usuario para
   * que no se filtre entre sesiones tras un logout (el SPA no recarga la pagina).
   * Se resuelven con inject() para evitar acoplar el constructor y posibles ciclos.
   */
  private resetDomainState() {
    this.aprobacionService.reset();
    this.notificacionService.reset();
    this.operadorCountsService.reset();
  }

  private setSession(res: AuthResponse) {
    // El access token NO se guarda: viaja en una cookie HttpOnly seteada por el
    // backend. Sólo persistimos el `user` (no secreto) para que la UI sobreviva al
    // reload sin pedir /auth/me; la cookie sobrevive el reload junto con él.
    localStorage.setItem(USER_KEY, JSON.stringify(res.user));
    this.currentUser.set(res.user);
  }

  private loadUser(): User | null {
    if (typeof localStorage === 'undefined') return null;
    const data = localStorage.getItem(USER_KEY);
    return data ? JSON.parse(data) : null;
  }
}
